import { isPhotoSegment, isVideoSegment, } from "@memento/types/segment";
import { notEmpty } from "./type-witchcraft.js";
import { getPluralEnding } from "./text.js";
import { getIsAudioOnlyPipeClip, getIsAudioOnlyPipeSnapshotUrl, } from "./pipe.js";
import { clampRange } from "./numbers.js";
/**
 * An "audio-only" segment is a video segment that has no video content. Pipe
 * has a couple (hacky) ways of indicating that a segment is audio-only that we
 * can check.
 */
export function getIsAudioOnlySegment(segment) {
    // photo segments cannot be audio-only
    if (!isVideoSegment(segment)) {
        return false;
    }
    else if (segment.metadata && getIsAudioOnlyPipeClip(segment.metadata)) {
        return true;
    }
    else if (getIsAudioOnlyPipeSnapshotUrl(segment.thumbUrl)) {
        return true;
    }
    else {
        return false;
    }
}
export function getSegmentDuration(segment) {
    if (isPhotoSegment(segment)) {
        return segment.durationSeconds;
    }
    else {
        const duration = segment.video_duration
            ? parseFloat(segment.video_duration)
            : segment.metadata?.data?.duration
                ? parseFloat(segment.metadata?.data?.duration)
                : 0;
        return duration;
    }
}
export function clampTrimRange({ start, end, }) {
    const clampedRange = {
        start: clampRange(start, { min: 0, max: 1 }),
        end: clampRange(end, { min: 0, max: 1 }),
    };
    if (clampedRange.end > clampedRange.start) {
        return clampedRange;
    }
    else {
        return { start: 0, end: 1 };
    }
}
export function segmentToRenderConfigClip(segment) {
    if (isPhotoSegment(segment)) {
        return {
            type: "photo",
            segmentId: segment.segmentId,
            durationSeconds: segment.durationSeconds,
            urls: segment.urls,
            width: segment.width,
            height: segment.height,
            rotateAngleDegrees: segment.edit.rotateAngle || 0,
            skipFace: Boolean(segment.edit.skipFace),
            caption: {
                title: segment.edit?.text?.header ?? undefined,
                subtitle: segment.edit?.text?.subHeader ?? undefined,
            },
            thumbnailSrc: segment.thumbUrl,
            isLocked: segment.isLocked,
        };
    }
    else {
        const { width, height } = getSegmentSize(segment);
        return {
            type: "video",
            segmentId: segment.segmentId,
            durationSeconds: getSegmentDuration(segment),
            urls: [segment.videoCdnUrl, segment.videoUrl].filter(notEmpty),
            width,
            height,
            rotateAngleDegrees: (segment.edit?.rotateAngle || 0),
            trimNormalized: clampTrimRange(segment.edit?.cutInfo?.cut
                ? {
                    start: segment.edit.cutInfo.start,
                    end: segment.edit.cutInfo.end,
                }
                : { start: 0, end: 1 }),
            skipFace: Boolean(segment.edit?.skipFace),
            muteMusic: Boolean(segment.edit?.muteMusic),
            caption: {
                title: segment.edit?.text?.header ?? undefined,
                subtitle: segment.edit?.text?.subHeader ?? undefined,
            },
            thumbnailSrc: segment.thumbUrl,
            isLocked: segment.isLocked,
        };
    }
}
export function getSegmentSize(segment) {
    if (isPhotoSegment(segment)) {
        return { width: segment.width, height: segment.height };
    }
    else {
        const width = segment.metadata?.data?.width
            ? Number(segment.metadata.data.width)
            : 1920;
        const height = segment.metadata?.data?.height
            ? Number(segment.metadata.data.height)
            : 1080;
        return { width, height };
    }
}
function getSegmentTypeHuman(segmentType, segmentTypeToHuman) {
    return segmentTypeToHuman[segmentType || "video"];
}
export function getSegmentTypeHumanLowercase(segmentType) {
    return getSegmentTypeHuman(segmentType, {
        photo: "photo",
        video: "video",
    });
}
export function getSegmentTypeHumanCapitalized(segmentType) {
    return getSegmentTypeHuman(segmentType, {
        photo: "Photo",
        video: "Video",
    });
}
export function getNotEnoughSegmentsToRenderMessage(minSegmentCountToRender) {
    return `You need at least ${minSegmentCountToRender} clip${getPluralEnding(minSegmentCountToRender)} to create a group video.`;
}
export function getIsEnoughSegmentsToRenderV3(segments, minSegmentCountToRender) {
    const activeSegments = segments.filter((segment) => !segment.isDeleted);
    return getIsEnoughSegmentsToRender({
        numSegments: activeSegments.length,
        minSegmentCountToRender,
    });
}
export function getIsEnoughSegmentsToRenderV2(segments, minSegmentCountToRender) {
    const activeVideoSegments = segments
        .filter((segment) => !segment.isDeleted)
        .filter(isVideoSegment);
    return getIsEnoughSegmentsToRender({
        numSegments: activeVideoSegments.length,
        minSegmentCountToRender,
    });
}
export function getIsEnoughSegmentsToRender({ numSegments, minSegmentCountToRender, }) {
    return numSegments >= minSegmentCountToRender;
}
// Pipe sets a "type" field in `segment.metadata.data.type`. Currently that
// field can function as an extension. However, if Pipe changes that (e.g.
// includes mime types), we don't want to blindingly use it as an extension.
// This is a list of types that Pipe uses that will work as an extension.
const knownPipeVideoTypesAsExtensions = ["mp4"];
// We can pull the extension from the URL, but we need to know that what we get
// from the URL is a known video extension. This is a list of known video
// extensions.
const knownVideoExtensions = ["mp4"];
function getExtensionFromUrl(url) {
    const urlParts = url.split(".");
    return urlParts[urlParts.length - 1];
}
function getVideoSegmentFileExtension(segment) {
    const pipeType = segment.metadata?.data?.type;
    if (pipeType && knownPipeVideoTypesAsExtensions.includes(pipeType)) {
        return pipeType;
    }
    const knownExtensionFromUrls = [segment.videoUrl, segment.videoCdnUrl]
        .filter(notEmpty)
        .map(getExtensionFromUrl)
        .filter(notEmpty)
        .filter((x) => knownVideoExtensions.includes(x));
    return knownExtensionFromUrls[0] || "mp4";
}
const knownPhotoMimeToExtension = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
};
// We can pull the extension from the URL, but we need to know that what we get
// from the URL is a known photo extension. This is a list of known photo
// extensions.
const knownPhotoExtensions = ["webp", "jpg", "jpeg", "png"];
function getPhotoSegmentFileExtension(segment) {
    if (segment.mimeType) {
        const maybeExtension = knownPhotoMimeToExtension[segment.mimeType];
        if (maybeExtension) {
            return maybeExtension;
        }
    }
    const knownExtensionFromUrls = segment.urls
        .filter(notEmpty)
        .map(getExtensionFromUrl)
        .filter(notEmpty)
        .filter((x) => knownPhotoExtensions.includes(x));
    return knownExtensionFromUrls[0] || "webp";
}
export function getSegmentFileExtension(segment) {
    if (isVideoSegment(segment)) {
        return getVideoSegmentFileExtension(segment);
    }
    else if (isPhotoSegment(segment)) {
        return getPhotoSegmentFileExtension(segment);
    }
}
/**
 * We need a function like this because the processingStatus prop was added
 * later and so sometimes processingStatus can be undefined. There are other
 * ways to determine the processing status, so we'll use those in the case where
 * processingStatus is undefined.
 */
export function getSegmentProcessingStatus(segment) {
    if (segment.processingStatus === "invalid") {
        return "invalid";
    }
    else if (segment.processingStatus === "processing") {
        return "processing";
    }
    else if (segment.processingStatus === "complete") {
        return "complete";
    }
    // video segments that are missing a videoUrl are still processing
    else if (isVideoSegment(segment) && !segment.videoUrl) {
        return "processing";
    }
    else if (getSegmentDuration(segment) <= 0 ||
        getIsAudioOnlySegment(segment)) {
        return "invalid";
    }
    else {
        return "complete";
    }
}
export function sortSegments(segments) {
    const segmentIdToProcessingStatus = {};
    function getSegmentProcessingStatusProxy(segment) {
        if (segmentIdToProcessingStatus[segment.segmentId]) {
            return segmentIdToProcessingStatus[segment.segmentId];
        }
        const processingStatus = getSegmentProcessingStatus(segment);
        segmentIdToProcessingStatus[segment.segmentId] = processingStatus;
        return processingStatus;
    }
    return segments.sort((a, b) => {
        const aProcessingStatus = getSegmentProcessingStatusProxy(a);
        const bProcessingStatus = getSegmentProcessingStatusProxy(b);
        const aInvalid = aProcessingStatus === "invalid";
        const bInvalid = bProcessingStatus === "invalid";
        // always put invalid segments last
        if (aInvalid && !bInvalid) {
            // A should come after B
            return 1;
        }
        else if (!aInvalid && bInvalid) {
            // A should come before B
            return -1;
        }
        // always put deleted segments second to last
        else if (a.isDeleted && !b.isDeleted) {
            // A should come after B
            return 1;
        }
        else if (!a.isDeleted && b.isDeleted) {
            // A should come before B
            return -1;
        }
        // if sortOrder is the same, sort by createdAt
        else if (a.sortOrder === b.sortOrder) {
            return a.createdAt - b.createdAt;
        }
        else {
            return a.sortOrder - b.sortOrder;
        }
    });
}
/**
 * Ensures that segment sortOrders will be in continuous order (starting at 0).
 * Should only be called on *all* segments for a card.
 *
 * NOTE: Will update the sortOrder in-place.
 */
export async function ensureSegmentContinuousSortOrderGeneral(
// should already be sorted
sortedSegments, 
// sortOrder will be updated in this segment object
onUpdate) {
    // start at -1 so that when we increment, the first sortOrder will be 0
    let expectedSortOrder = -1;
    for (const segment of sortedSegments) {
        expectedSortOrder++;
        if (segment.sortOrder !== expectedSortOrder) {
            segment.sortOrder = expectedSortOrder;
            await onUpdate(segment);
        }
    }
    return sortedSegments;
}
/** This only applies to video segments, any other segment types will
 *  return false.  */
export function getIsVideoSegmentDurationTooShort(segment, minDurationSeconds) {
    return (isVideoSegment(segment) && getSegmentDuration(segment) < minDurationSeconds);
}
/**
 * Returns allSegments with an isLocked property set.
 */
export function getSegmentsWithIsLocked(args) {
    const lockedSegments = getLockedSegments(args);
    const lockedSegmentIds = new Set(lockedSegments.map((s) => s.segmentId));
    const segmentsWithIsLocked = [...args.allSegments].map((s) => ({
        ...s,
        isLocked: lockedSegmentIds.has(s.segmentId),
    }));
    return segmentsWithIsLocked;
}
export function getLockedSegments({ allSegments, numUnlockedSegments, canManageAllSegments, featureFlagGroupVideosPaywallMovedToRender, }) {
    // if we don't limit the number of segments the user can manage, don't lock
    // any segments
    if (numUnlockedSegments < 0) {
        return [];
    }
    // if the user can manage all the segments, don't lock any
    if (canManageAllSegments) {
        return [];
    }
    // the user can't VideoClipManage, so if the feature flag is off, we want to
    // lock all the segments
    if (!featureFlagGroupVideosPaywallMovedToRender) {
        return [...allSegments];
    }
    // we want to lock the first N segments, based on when they were created,
    // regardless of whether they're outtakes. So we need to sort them by
    // creation date.
    const sortedSegments = [...allSegments].sort((a, b) => a.createdAt - b.createdAt);
    // The first N segments will be unlocked, the rest locked, regardless of
    // whether they're outtakes
    const lockedSegments = sortedSegments.slice(numUnlockedSegments);
    return lockedSegments;
}
